import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import nexusApi from '../../services/nexusApi'
import { Activity, Flag, Bell, FileText, CheckCircle, XCircle, AlertTriangle, ToggleLeft, ToggleRight, Plus, X, Settings, Eye, EyeOff, Send, RefreshCw } from 'lucide-react'

const NX = {
  bg: '#0D1117', surface: '#161B22', surface2: '#1C2330', border: '#2A3441',
  text: '#E6EDF3', textDim: '#8B949E', accent: '#0066FF',
  green: '#3DB54A', amber: '#F5921E', red: '#EF4444',
}

function StatusBadge({ status }: { status: string }) {
  const ok = status === 'ok'
  const warn = status === 'degraded' || status === 'not_configured'
  const color = ok ? NX.green : warn ? NX.amber : NX.red
  const Icon = ok ? CheckCircle : warn ? AlertTriangle : XCircle
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color, fontSize: '0.8125rem', fontWeight: 600 }}>
      <Icon size={14} /> {status.replace('_', ' ')}
    </span>
  )
}

function Card({ children, style = {} }: any) {
  return (
    <div style={{ background: NX.surface, border: `1px solid ${NX.border}`, borderRadius: 12, padding: '1.25rem', ...style }}>
      {children}
    </div>
  )
}

function timeAgo(date: string) {
  const d = Date.now() - new Date(date).getTime()
  const m = Math.floor(d / 60000)
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  return `${Math.floor(h / 24)}d ago`
}

export default function NexusPlatformHealth() {
  const { tab } = useParams<{ tab: string }>()
  const [health, setHealth] = useState<any>(null)
  const [errors, setErrors] = useState<any[]>([])
  const [flags, setFlags] = useState<any[]>([])
  const [announcements, setAnnouncements] = useState<any[]>([])
  const [auditLog, setAuditLog] = useState<any[]>([])
  const [auditTotal, setAuditTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [showAnnouncementForm, setShowAnnouncementForm] = useState(false)
  const [newAnn, setNewAnn] = useState({ title: '', body: '', type: 'feature', target: 'all' })

  // Config tab state
  const [cfg, setCfg] = useState<any>({})
  const [cfgForm, setCfgForm] = useState<any>({})
  const [cfgSaving, setCfgSaving] = useState(false)
  const [cfgMsg, setCfgMsg] = useState('')
  const [showKeys, setShowKeys] = useState<Record<string, boolean>>({})
  const [testEmail, setTestEmail] = useState('')
  const [testPhone, setTestPhone] = useState('')
  const [testingEmail, setTestingEmail] = useState(false)
  const [testingSMS, setTestingSMS] = useState(false)
  const [testMsg, setTestMsg] = useState<{ type: 'ok' | 'err'; text: string } | null>(null)

  useEffect(() => {
    const loads: Promise<any>[] = [nexusApi.get('/platform/health')]
    if (tab === 'health') loads.push(nexusApi.get('/platform/errors'))
    if (tab === 'flags') loads.push(nexusApi.get('/platform/feature-flags'))
    if (tab === 'announcements') loads.push(nexusApi.get('/platform/announcements'))
    if (tab === 'audit') loads.push(nexusApi.get('/platform/audit-log?limit=100'))
    if (tab === 'config') loads.push(nexusApi.get('/platform/config'))

    Promise.all(loads).then(results => {
      setHealth(results[0].data)
      if (tab === 'health' && results[1]) setErrors(results[1].data.errors || [])
      if (tab === 'flags' && results[1]) setFlags(results[1].data.flags || [])
      if (tab === 'announcements' && results[1]) setAnnouncements(results[1].data.announcements || [])
      if (tab === 'audit' && results[1]) {
        setAuditLog(results[1].data.entries || [])
        setAuditTotal(results[1].data.total || 0)
      }
      if (tab === 'config' && results[1]) {
        setCfg(results[1].data)
        setCfgForm({
          sendgrid_from_email: results[1].data.sendgrid_from_email || '',
          sendgrid_from_name: results[1].data.sendgrid_from_name || '',
        })
      }
    }).catch(() => {}).finally(() => setLoading(false))
  }, [tab])

  const toggleFlag = async (flag: any) => {
    try {
      const { data } = await nexusApi.patch(`/platform/feature-flags/${flag.id}`, { enabled_globally: flag.enabled_globally ? 0 : 1 })
      setFlags(fl => fl.map(f => f.id === flag.id ? data : f))
    } catch {}
  }

  const createAnnouncement = async () => {
    if (!newAnn.title || !newAnn.body) return
    try {
      await nexusApi.post('/platform/announcements', newAnn)
      const { data } = await nexusApi.get('/platform/announcements')
      setAnnouncements(data.announcements || [])
      setShowAnnouncementForm(false)
      setNewAnn({ title: '', body: '', type: 'feature', target: 'all' })
    } catch {}
  }

  const saveConfig = async (section: 'sendgrid' | 'twilio') => {
    setCfgSaving(true); setCfgMsg('')
    try {
      await nexusApi.patch('/platform/config', cfgForm)
      const { data } = await nexusApi.get('/platform/config')
      setCfg(data)
      setCfgMsg('Saved')
      setTimeout(() => setCfgMsg(''), 3000)
    } catch (err: any) {
      setCfgMsg(err.response?.data?.error || 'Save failed')
    } finally { setCfgSaving(false) }
  }

  const runTestEmail = async () => {
    if (!testEmail) return
    setTestingEmail(true); setTestMsg(null)
    try {
      await nexusApi.post('/platform/config/test-email', { to: testEmail })
      setTestMsg({ type: 'ok', text: `Test email sent to ${testEmail}` })
    } catch (err: any) {
      setTestMsg({ type: 'err', text: err.response?.data?.error || 'Test failed' })
    } finally { setTestingEmail(false) }
  }

  const runTestSMS = async () => {
    if (!testPhone) return
    setTestingSMS(true); setTestMsg(null)
    try {
      await nexusApi.post('/platform/config/test-sms', { to: testPhone })
      setTestMsg({ type: 'ok', text: `Test SMS sent to ${testPhone}` })
    } catch (err: any) {
      setTestMsg({ type: 'err', text: err.response?.data?.error || 'Test failed' })
    } finally { setTestingSMS(false) }
  }

  const TAB_ICONS: Record<string, any> = { health: Activity, flags: Flag, announcements: Bell, audit: FileText, config: Settings }

  return (
    <div style={{ padding: '2rem', maxWidth: 1100 }}>
      <div style={{ marginBottom: '1.75rem' }}>
        <h1 style={{ color: NX.text, fontSize: '1.375rem', fontWeight: 700, margin: 0 }}>Platform</h1>
      </div>

      {/* Tab nav */}
      <div style={{ display: 'flex', gap: 4, marginBottom: '1.5rem', borderBottom: `1px solid ${NX.border}`, paddingBottom: 0 }}>
        {[
          { id: 'health', label: 'Health & Errors' },
          { id: 'flags', label: 'Feature Flags' },
          { id: 'announcements', label: 'Announcements' },
          { id: 'audit', label: 'Audit Log' },
          { id: 'config', label: 'Config' },
        ].map(t => {
          const Icon = TAB_ICONS[t.id]
          const active = tab === t.id
          return (
            <a key={t.id} href={`/nexus/platform/${t.id}`} style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '0.625rem 1rem', textDecoration: 'none',
              borderBottom: active ? `2px solid ${NX.accent}` : '2px solid transparent',
              color: active ? NX.accent : NX.textDim, fontSize: '0.875rem', fontWeight: active ? 600 : 400,
              marginBottom: -1,
            }}>
              <Icon size={14} /> {t.label}
            </a>
          )
        })}
      </div>

      {loading ? (
        <div style={{ color: NX.textDim, textAlign: 'center', padding: '3rem 0' }}>Loading…</div>
      ) : (
        <>
          {/* HEALTH TAB */}
          {tab === 'health' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
              {/* Services */}
              <Card>
                <h2 style={{ color: NX.text, fontWeight: 600, margin: '0 0 1rem', fontSize: '1rem' }}>Service Status</h2>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '0.75rem' }}>
                  {Object.entries(health?.services || {}).map(([key, svc]: [string, any]) => (
                    <div key={key} style={{ background: NX.surface2, borderRadius: 10, padding: '0.875rem', border: `1px solid ${NX.border}` }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                        <span style={{ color: NX.text, fontWeight: 600, fontSize: '0.875rem', textTransform: 'capitalize' }}>{key.replace('_', ' ')}</span>
                        <StatusBadge status={svc.status} />
                      </div>
                      {svc.stats && (
                        <div style={{ display: 'flex', gap: 12 }}>
                          {Object.entries(svc.stats).map(([k, v]: [string, any]) => (
                            <span key={k} style={{ color: NX.textDim, fontSize: '0.6875rem' }}>{k}: <b style={{ color: NX.text }}>{v}</b></span>
                          ))}
                        </div>
                      )}
                      {svc.note && <div style={{ color: NX.textDim, fontSize: '0.6875rem', marginTop: 4 }}>{svc.note}</div>}
                      {svc.latency && <div style={{ color: NX.textDim, fontSize: '0.6875rem', marginTop: 4 }}>Latency: {svc.latency}</div>}
                    </div>
                  ))}
                </div>
              </Card>

              {/* Error log */}
              <Card>
                <h2 style={{ color: NX.text, fontWeight: 600, margin: '0 0 1rem', fontSize: '1rem' }}>Recent Errors</h2>
                {errors.length === 0 ? (
                  <div style={{ color: NX.textDim, textAlign: 'center', padding: '2rem 0', fontSize: '0.875rem' }}>
                    <CheckCircle size={28} color={NX.green} style={{ display: 'block', margin: '0 auto 8px' }} />
                    No errors logged
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {errors.slice(0, 20).map((err: any, i) => (
                      <div key={i} style={{ background: NX.surface2, borderRadius: 8, padding: '0.625rem 0.875rem', borderLeft: `3px solid ${NX.red}` }}>
                        <div style={{ color: NX.text, fontSize: '0.8125rem', fontFamily: 'monospace' }}>{err.message || err.error || JSON.stringify(err).slice(0, 120)}</div>
                        {err.timestamp && <div style={{ color: NX.textDim, fontSize: '0.6875rem', marginTop: 2 }}>{err.timestamp}</div>}
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            </div>
          )}

          {/* FLAGS TAB */}
          {tab === 'flags' && (
            <Card>
              <h2 style={{ color: NX.text, fontWeight: 600, margin: '0 0 1rem', fontSize: '1rem' }}>Feature Flags</h2>
              {flags.length === 0 ? (
                <div style={{ color: NX.textDim, textAlign: 'center', padding: '2rem 0', fontSize: '0.875rem' }}>No feature flags configured</div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: `1px solid ${NX.border}` }}>
                      {['Feature', 'Key', 'Status', 'Rollout', 'Toggle'].map(h => (
                        <th key={h} style={{ color: NX.textDim, fontSize: '0.75rem', fontWeight: 600, textAlign: 'left', padding: '0.5rem 0.75rem' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {flags.map(flag => (
                      <tr key={flag.id} style={{ borderBottom: `1px solid ${NX.border}` }}>
                        <td style={{ padding: '0.75rem', color: NX.text, fontSize: '0.875rem' }}>
                          <div style={{ fontWeight: 500 }}>{flag.name}</div>
                          {flag.description && <div style={{ color: NX.textDim, fontSize: '0.75rem' }}>{flag.description}</div>}
                        </td>
                        <td style={{ padding: '0.75rem', fontFamily: 'monospace', color: NX.textDim, fontSize: '0.8125rem' }}>{flag.key}</td>
                        <td style={{ padding: '0.75rem' }}>
                          <span style={{
                            background: flag.enabled_globally ? `${NX.green}22` : `${NX.textDim}22`,
                            color: flag.enabled_globally ? NX.green : NX.textDim,
                            fontSize: '0.75rem', fontWeight: 700, padding: '2px 8px', borderRadius: 99,
                          }}>
                            {flag.enabled_globally ? 'ENABLED' : 'DISABLED'}
                          </span>
                        </td>
                        <td style={{ padding: '0.75rem', color: NX.textDim, fontSize: '0.875rem' }}>{flag.rollout_percentage}%</td>
                        <td style={{ padding: '0.75rem' }}>
                          <button onClick={() => toggleFlag(flag)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: flag.enabled_globally ? NX.green : NX.textDim }}>
                            {flag.enabled_globally ? <ToggleRight size={24} /> : <ToggleLeft size={24} />}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Card>
          )}

          {/* ANNOUNCEMENTS TAB */}
          {tab === 'announcements' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button onClick={() => setShowAnnouncementForm(true)} style={{
                  display: 'flex', alignItems: 'center', gap: 6, background: NX.accent,
                  color: '#fff', border: 'none', borderRadius: 8, padding: '0.5rem 1rem',
                  fontSize: '0.875rem', fontWeight: 600, cursor: 'pointer',
                }}>
                  <Plus size={14} /> New Announcement
                </button>
              </div>
              {showAnnouncementForm && (
                <Card>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                    <h3 style={{ color: NX.text, margin: 0, fontWeight: 600 }}>Create Announcement</h3>
                    <button onClick={() => setShowAnnouncementForm(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: NX.textDim }}><X size={16} /></button>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <input value={newAnn.title} onChange={e => setNewAnn(a => ({ ...a, title: e.target.value }))} placeholder="Title"
                      style={{ background: NX.surface2, border: `1px solid ${NX.border}`, borderRadius: 8, color: NX.text, padding: '0.5rem 0.75rem', fontSize: '0.875rem' }} />
                    <textarea value={newAnn.body} onChange={e => setNewAnn(a => ({ ...a, body: e.target.value }))} placeholder="Body…" rows={3}
                      style={{ background: NX.surface2, border: `1px solid ${NX.border}`, borderRadius: 8, color: NX.text, padding: '0.5rem 0.75rem', fontSize: '0.875rem', resize: 'vertical' }} />
                    <div style={{ display: 'flex', gap: 8 }}>
                      <select value={newAnn.type} onChange={e => setNewAnn(a => ({ ...a, type: e.target.value }))}
                        style={{ background: NX.surface2, border: `1px solid ${NX.border}`, borderRadius: 8, color: NX.text, padding: '0.5rem 0.75rem', fontSize: '0.875rem' }}>
                        <option value="feature">Feature</option>
                        <option value="maintenance">Maintenance</option>
                        <option value="alert">Alert</option>
                        <option value="billing">Billing</option>
                      </select>
                      <select value={newAnn.target} onChange={e => setNewAnn(a => ({ ...a, target: e.target.value }))}
                        style={{ background: NX.surface2, border: `1px solid ${NX.border}`, borderRadius: 8, color: NX.text, padding: '0.5rem 0.75rem', fontSize: '0.875rem' }}>
                        <option value="all">All tenants</option>
                        <option value="plan">By plan</option>
                        <option value="industry">By industry</option>
                      </select>
                      <button onClick={createAnnouncement} style={{ background: NX.accent, color: '#fff', border: 'none', borderRadius: 8, padding: '0.5rem 1rem', fontWeight: 600, cursor: 'pointer', fontSize: '0.875rem' }}>Post</button>
                    </div>
                  </div>
                </Card>
              )}
              {announcements.length === 0 ? (
                <Card>
                  <div style={{ color: NX.textDim, textAlign: 'center', padding: '2rem 0', fontSize: '0.875rem' }}>No announcements yet</div>
                </Card>
              ) : announcements.map(ann => {
                const typeColors: Record<string, string> = { feature: NX.accent, maintenance: NX.amber, alert: NX.red, billing: NX.green }
                return (
                  <Card key={ann.id}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 8 }}>
                      <h3 style={{ color: NX.text, margin: 0, fontWeight: 600, fontSize: '0.9375rem' }}>{ann.title}</h3>
                      <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                        <span style={{ background: `${typeColors[ann.type] || NX.accent}22`, color: typeColors[ann.type] || NX.accent, fontSize: '0.6875rem', fontWeight: 700, padding: '2px 7px', borderRadius: 99, textTransform: 'uppercase' }}>{ann.type}</span>
                        <span style={{ background: `${NX.textDim}22`, color: NX.textDim, fontSize: '0.6875rem', fontWeight: 600, padding: '2px 7px', borderRadius: 99 }}>{ann.target}</span>
                      </div>
                    </div>
                    <p style={{ color: NX.textDim, fontSize: '0.875rem', margin: '0 0 8px', lineHeight: 1.6 }}>{ann.body}</p>
                    <div style={{ color: NX.textDim, fontSize: '0.6875rem' }}>
                      By {ann.created_by_name || 'System'} · {new Date(ann.created_at).toLocaleDateString('en-AU')}
                    </div>
                  </Card>
                )
              })}
            </div>
          )}

          {/* CONFIG TAB */}
          {tab === 'config' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', maxWidth: 680 }}>

              {/* SendGrid */}
              <Card>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: '1.25rem' }}>
                  <div style={{ width: 32, height: 32, borderRadius: 8, background: '#1A82E222', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Send size={15} color="#1A82E2" />
                  </div>
                  <div>
                    <h2 style={{ color: NX.text, fontWeight: 700, margin: 0, fontSize: '1rem' }}>SendGrid</h2>
                    <p style={{ color: NX.textDim, fontSize: '0.75rem', margin: 0 }}>Platform email delivery</p>
                  </div>
                  <span style={{
                    marginLeft: 'auto', padding: '3px 10px', borderRadius: 99, fontSize: '0.6875rem', fontWeight: 700,
                    background: cfg.sendgrid_api_key_set ? `${NX.green}22` : `${NX.amber}22`,
                    color: cfg.sendgrid_api_key_set ? NX.green : NX.amber,
                  }}>{cfg.sendgrid_api_key_set ? 'CONFIGURED' : 'NOT SET'}</span>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {/* API Key */}
                  <div>
                    <label style={{ color: NX.textDim, fontSize: '0.8125rem', display: 'block', marginBottom: 4 }}>API Key</label>
                    <div style={{ position: 'relative' }}>
                      <input
                        type={showKeys.sg ? 'text' : 'password'}
                        placeholder={cfg.sendgrid_api_key_set ? cfg.sendgrid_api_key || '••••••••' : 'SG.xxxxxxxx'}
                        onChange={e => setCfgForm((f: any) => ({ ...f, sendgrid_api_key: e.target.value }))}
                        style={{ background: NX.surface2, border: `1px solid ${NX.border}`, borderRadius: 8, padding: '0.5rem 2.5rem 0.5rem 0.75rem', color: NX.text, fontSize: '0.875rem', width: '100%', boxSizing: 'border-box' as const }}
                      />
                      <button onClick={() => setShowKeys(k => ({ ...k, sg: !k.sg }))} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: NX.textDim }}>
                        {showKeys.sg ? <EyeOff size={14} /> : <Eye size={14} />}
                      </button>
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                    <div>
                      <label style={{ color: NX.textDim, fontSize: '0.8125rem', display: 'block', marginBottom: 4 }}>From Email</label>
                      <input
                        value={cfgForm.sendgrid_from_email || ''}
                        onChange={e => setCfgForm((f: any) => ({ ...f, sendgrid_from_email: e.target.value }))}
                        placeholder="notifications@hseqintelligence.com.au"
                        style={{ background: NX.surface2, border: `1px solid ${NX.border}`, borderRadius: 8, padding: '0.5rem 0.75rem', color: NX.text, fontSize: '0.875rem', width: '100%', boxSizing: 'border-box' as const }}
                      />
                    </div>
                    <div>
                      <label style={{ color: NX.textDim, fontSize: '0.8125rem', display: 'block', marginBottom: 4 }}>From Name</label>
                      <input
                        value={cfgForm.sendgrid_from_name || ''}
                        onChange={e => setCfgForm((f: any) => ({ ...f, sendgrid_from_name: e.target.value }))}
                        placeholder="HSEQ Intelligence"
                        style={{ background: NX.surface2, border: `1px solid ${NX.border}`, borderRadius: 8, padding: '0.5rem 0.75rem', color: NX.text, fontSize: '0.875rem', width: '100%', boxSizing: 'border-box' as const }}
                      />
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <button onClick={() => saveConfig('sendgrid')} disabled={cfgSaving} style={{ background: NX.accent, color: '#fff', border: 'none', borderRadius: 8, padding: '0.5rem 1.25rem', fontWeight: 600, fontSize: '0.875rem', cursor: 'pointer', opacity: cfgSaving ? 0.7 : 1 }}>
                      {cfgSaving ? 'Saving…' : 'Save'}
                    </button>
                    {cfgMsg && <span style={{ color: cfgMsg === 'Saved' ? NX.green : NX.red, fontSize: '0.8125rem' }}>{cfgMsg}</span>}
                  </div>

                  {/* Test */}
                  {cfg.sendgrid_api_key_set && (
                    <div style={{ borderTop: `1px solid ${NX.border}`, paddingTop: '0.875rem', marginTop: 4 }}>
                      <label style={{ color: NX.textDim, fontSize: '0.8125rem', display: 'block', marginBottom: 4 }}>Send test email to</label>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <input
                          type="email"
                          value={testEmail}
                          onChange={e => setTestEmail(e.target.value)}
                          placeholder="you@example.com"
                          style={{ flex: 1, background: NX.surface2, border: `1px solid ${NX.border}`, borderRadius: 8, padding: '0.5rem 0.75rem', color: NX.text, fontSize: '0.875rem' }}
                        />
                        <button onClick={runTestEmail} disabled={testingEmail || !testEmail} style={{ background: NX.surface2, border: `1px solid ${NX.border}`, color: NX.text, borderRadius: 8, padding: '0.5rem 1rem', fontSize: '0.875rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, opacity: !testEmail ? 0.5 : 1 }}>
                          {testingEmail ? <RefreshCw size={13} style={{ animation: 'spin 1s linear infinite' }} /> : <Send size={13} />} Test
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </Card>

              {/* Twilio */}
              <Card>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: '1.25rem' }}>
                  <div style={{ width: 32, height: 32, borderRadius: 8, background: '#F2271422', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Send size={15} color="#F22714" />
                  </div>
                  <div>
                    <h2 style={{ color: NX.text, fontWeight: 700, margin: 0, fontSize: '1rem' }}>Twilio</h2>
                    <p style={{ color: NX.textDim, fontSize: '0.75rem', margin: 0 }}>Platform SMS delivery</p>
                  </div>
                  <span style={{
                    marginLeft: 'auto', padding: '3px 10px', borderRadius: 99, fontSize: '0.6875rem', fontWeight: 700,
                    background: cfg.twilio_account_sid_set ? `${NX.green}22` : `${NX.amber}22`,
                    color: cfg.twilio_account_sid_set ? NX.green : NX.amber,
                  }}>{cfg.twilio_account_sid_set ? 'CONFIGURED' : 'NOT SET'}</span>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                    <div>
                      <label style={{ color: NX.textDim, fontSize: '0.8125rem', display: 'block', marginBottom: 4 }}>Account SID</label>
                      <div style={{ position: 'relative' }}>
                        <input
                          type={showKeys.tSid ? 'text' : 'password'}
                          placeholder={cfg.twilio_account_sid_set ? cfg.twilio_account_sid || '••••••••' : 'ACxxxxxxxx'}
                          onChange={e => setCfgForm((f: any) => ({ ...f, twilio_account_sid: e.target.value }))}
                          style={{ background: NX.surface2, border: `1px solid ${NX.border}`, borderRadius: 8, padding: '0.5rem 2.5rem 0.5rem 0.75rem', color: NX.text, fontSize: '0.875rem', width: '100%', boxSizing: 'border-box' as const }}
                        />
                        <button onClick={() => setShowKeys(k => ({ ...k, tSid: !k.tSid }))} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: NX.textDim }}>
                          {showKeys.tSid ? <EyeOff size={14} /> : <Eye size={14} />}
                        </button>
                      </div>
                    </div>
                    <div>
                      <label style={{ color: NX.textDim, fontSize: '0.8125rem', display: 'block', marginBottom: 4 }}>Auth Token</label>
                      <div style={{ position: 'relative' }}>
                        <input
                          type={showKeys.tTok ? 'text' : 'password'}
                          placeholder={cfg.twilio_auth_token_set ? '••••••••' : 'auth token'}
                          onChange={e => setCfgForm((f: any) => ({ ...f, twilio_auth_token: e.target.value }))}
                          style={{ background: NX.surface2, border: `1px solid ${NX.border}`, borderRadius: 8, padding: '0.5rem 2.5rem 0.5rem 0.75rem', color: NX.text, fontSize: '0.875rem', width: '100%', boxSizing: 'border-box' as const }}
                        />
                        <button onClick={() => setShowKeys(k => ({ ...k, tTok: !k.tTok }))} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: NX.textDim }}>
                          {showKeys.tTok ? <EyeOff size={14} /> : <Eye size={14} />}
                        </button>
                      </div>
                    </div>
                  </div>
                  <div>
                    <label style={{ color: NX.textDim, fontSize: '0.8125rem', display: 'block', marginBottom: 4 }}>From Phone Number</label>
                    <input
                      placeholder={cfg.twilio_phone_number || '+61400000000'}
                      onChange={e => setCfgForm((f: any) => ({ ...f, twilio_phone_number: e.target.value }))}
                      style={{ background: NX.surface2, border: `1px solid ${NX.border}`, borderRadius: 8, padding: '0.5rem 0.75rem', color: NX.text, fontSize: '0.875rem', width: '100%', boxSizing: 'border-box' as const }}
                    />
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <button onClick={() => saveConfig('twilio')} disabled={cfgSaving} style={{ background: NX.accent, color: '#fff', border: 'none', borderRadius: 8, padding: '0.5rem 1.25rem', fontWeight: 600, fontSize: '0.875rem', cursor: 'pointer', opacity: cfgSaving ? 0.7 : 1 }}>
                      {cfgSaving ? 'Saving…' : 'Save'}
                    </button>
                    {cfgMsg && <span style={{ color: cfgMsg === 'Saved' ? NX.green : NX.red, fontSize: '0.8125rem' }}>{cfgMsg}</span>}
                  </div>

                  {/* Test */}
                  {cfg.twilio_account_sid_set && (
                    <div style={{ borderTop: `1px solid ${NX.border}`, paddingTop: '0.875rem', marginTop: 4 }}>
                      <label style={{ color: NX.textDim, fontSize: '0.8125rem', display: 'block', marginBottom: 4 }}>Send test SMS to</label>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <input
                          type="tel"
                          value={testPhone}
                          onChange={e => setTestPhone(e.target.value)}
                          placeholder="+61400000000"
                          style={{ flex: 1, background: NX.surface2, border: `1px solid ${NX.border}`, borderRadius: 8, padding: '0.5rem 0.75rem', color: NX.text, fontSize: '0.875rem' }}
                        />
                        <button onClick={runTestSMS} disabled={testingSMS || !testPhone} style={{ background: NX.surface2, border: `1px solid ${NX.border}`, color: NX.text, borderRadius: 8, padding: '0.5rem 1rem', fontSize: '0.875rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, opacity: !testPhone ? 0.5 : 1 }}>
                          {testingSMS ? <RefreshCw size={13} style={{ animation: 'spin 1s linear infinite' }} /> : <Send size={13} />} Test
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Test result */}
                  {testMsg && (
                    <div style={{ padding: '0.625rem 0.875rem', borderRadius: 8, background: testMsg.type === 'ok' ? `${NX.green}18` : `${NX.red}18`, color: testMsg.type === 'ok' ? NX.green : NX.red, fontSize: '0.8125rem', display: 'flex', alignItems: 'center', gap: 6 }}>
                      {testMsg.type === 'ok' ? <CheckCircle size={14} /> : <XCircle size={14} />} {testMsg.text}
                    </div>
                  )}
                </div>
              </Card>

            </div>
          )}

          {/* AUDIT LOG TAB */}
          {tab === 'audit' && (
            <Card>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                <h2 style={{ color: NX.text, fontWeight: 600, margin: 0, fontSize: '1rem' }}>Audit Log</h2>
                <span style={{ color: NX.textDim, fontSize: '0.75rem' }}>{auditTotal} entries (immutable)</span>
              </div>
              {auditLog.length === 0 ? (
                <div style={{ color: NX.textDim, textAlign: 'center', padding: '2rem 0', fontSize: '0.875rem' }}>No audit entries yet</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {auditLog.map(entry => (
                    <div key={entry.id} style={{
                      display: 'flex', alignItems: 'flex-start', gap: 12,
                      padding: '0.625rem 0.75rem', borderRadius: 6,
                      background: NX.surface2,
                    }}>
                      <div style={{
                        width: 6, height: 6, borderRadius: '50%', background: NX.accent,
                        flexShrink: 0, marginTop: 6,
                      }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <span style={{ color: NX.text, fontSize: '0.8125rem', fontWeight: 600 }}>{entry.super_admin_name}</span>
                          <span style={{ background: `${NX.accent}22`, color: NX.accent, fontSize: '0.6875rem', fontWeight: 700, padding: '1px 6px', borderRadius: 4, fontFamily: 'monospace' }}>{entry.action}</span>
                          {entry.resource_type && <span style={{ color: NX.textDim, fontSize: '0.75rem' }}>{entry.resource_type}</span>}
                          {entry.tenant_id && <span style={{ color: NX.textDim, fontSize: '0.75rem' }}>tenant:{entry.tenant_id.slice(0, 8)}</span>}
                        </div>
                        <div style={{ color: NX.textDim, fontSize: '0.6875rem', marginTop: 2 }}>
                          {new Date(entry.created_at).toLocaleString('en-AU')} {entry.ip_address && `· ${entry.ip_address}`}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          )}
        </>
      )}
    </div>
  )
}
